import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// react admin
import {
  Datagrid,
  List,
  NumberField,
  TextField,
  FunctionField,
  TopToolbar,
  useListContext,
  useListController,
  useGetIdentity,
} from "react-admin";
// mui
import {
  Button,
  Box,
  Typography,
  TextField as MuiTextField,
  Chip,
  Menu,
  Alert,
  MenuItem,
  ListItemIcon,
  Select,
  InputLabel,
  FormControl,
  LinearProgress,
  Snackbar,
  useTheme,
  useMediaQuery,
  CircularProgress
} from "@mui/material";
import { Switch, FormControlLabel } from "@mui/material";

// mui icon
import GetAppIcon from "@mui/icons-material/GetApp";
import BackupTableIcon from "@mui/icons-material/BackupTable";
// component
import CustomPagination from "../../Components/CustomPagination";
import { Summary } from "./Summary/Summary";
// loader
import { Loader } from "../Loader";
// utils
import { gameCoinsSummary } from "../../Utilities/summary";
import { gamesCoinsExports } from "../../Utilities/allExports";
// xlsx export
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Autocomplete from "@mui/material/Autocomplete";
import Parse from "parse";

const CustomFilter = ({ canMarkTestUsers }) => {
  const { setFilters } = useListContext();
  const { data: userIdentity } = useGetIdentity();

  const [gameVariant, setGameVariant] = useState("All");
  const [gameName, setGameName] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const oneMonthAgo = new Date();
  oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);
  const defaultStartDate = oneMonthAgo.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(today);
  const [includeTestingUsers, setIncludeTestingUsers] = useState(false);

  // 🔍 Manager filter states
  const [managerValue, setManagerValue] = useState(null);
  const [managerOptions, setManagerOptions] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(false);

  const handleManagerSearch = async (inputValue) => {
    if (!inputValue) {
      setManagerOptions([]);
      return;
    }
    setLoadingManagers(true);
    try {
      const query = new Parse.Query(Parse.User);
      query.limit(20);
      query.select(["username", "role"]);
      query.include("role");
      query.matches("username", inputValue, "i");

      // exclude Player role
      const roleQuery = new Parse.Query(Parse.Role);
      roleQuery.notEqualTo("name", "Player");
      query.matchesQuery("role", roleQuery);

      const results = await query.find();
      const users = results.map((u) => ({
        id: u.id,
        username: u.get("username"),
        role: u.get("role")?.get("name") || "Unknown",
      }));
      setManagerOptions(users);
    } catch (err) {
      console.error("Manager search failed:", err);
    } finally {
      setLoadingManagers(false);
    }
  };

  // apply filters
  const handleApply = () => {
    const filters = {};
    if (gameVariant !== "All") filters.gameVariant = gameVariant;
    if (gameName) filters.gameName = gameName;
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;
    filters.includeTestingUsers = includeTestingUsers;
    if (managerValue) filters.manager = managerValue.id;

    setFilters(filters, {});
  };

  const handleClear = () => {
    setGameVariant("All");
    setGameName("");
    setStartDate("");
    setEndDate("");
    setIncludeTestingUsers(false);
    setManagerValue(null);
    setManagerOptions([]);
    setFilters({}, {});
  };

  useEffect(() => {
    // auto apply default filters once on mount
    const initialFilters = {
      startDate: defaultStartDate,
      endDate: today,
      includeTestingUsers: false,
    };
  
    setFilters(initialFilters, {});
  }, []); // run once
  
  return (
    <Box
      display={{ xs: "block", width: "100%", sm: "flex" }}
      alignItems="center"
      gap={2}
    >
      <Box display="flex" flexWrap="wrap" gap={2}>
        {/* Game Name */}
        <MuiTextField
          size="small"
          variant="outlined"
          value={gameName}
          onChange={(e) => setGameName(e.target.value)}
          placeholder="Game Name"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleApply();
            }
          }}
        />

        {/* Game Variant (only for Super-User) */}
        {userIdentity?.userRoleName === "Super-User" && (
          <FormControl>
            <InputLabel>Game</InputLabel>
            <Select
              size="small"
              value={gameVariant}
              label="Game"
              onChange={(e) => setGameVariant(e.target.value)}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="VE">VE</MenuItem>
              <MenuItem value="TD">TD</MenuItem>
            </Select>
          </FormControl>
        )}

        {/* Date Filters */}
        <MuiTextField
          size="small"
          type="date"
          label="Start Date"
          InputLabelProps={{ shrink: true }}
          value={startDate}
          onChange={(e) => {
            const newStart = e.target.value;
            setStartDate(newStart);
            if (endDate && newStart > endDate) {
              setEndDate("");
            }
          }}
          inputProps={{ max: today }}
        />
        <MuiTextField
          size="small"
          type="date"
          label="End Date"
          InputLabelProps={{ shrink: true }}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          inputProps={{
            min: startDate || undefined,
            max: today,
          }}
        />

        {/* Manager filter */}
        <Autocomplete
          size="small"
          sx={{ minWidth: 200 }}
          options={managerOptions}
          getOptionLabel={(option) => `${option.username} (${option.role})`}
          loading={loadingManagers}
          onInputChange={(e, newValue) => handleManagerSearch(newValue)}
          onChange={(e, newValue) => setManagerValue(newValue)}
          value={managerValue}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label="Search User"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingManagers ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        {/* Include Testing Users Switch (only for Super-User or Game Owner with permission) */}
        {(userIdentity?.userRoleName === "Super-User" || canMarkTestUsers) && (
          <FormControlLabel
            control={
              <Switch
                checked={includeTestingUsers}
                onChange={(e) => setIncludeTestingUsers(e.target.checked)}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#ffca28",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#ffca28",
                  },
                  "& .MuiSwitch-track": {
                    backgroundColor: "#555",
                  },
                }}
              />
            }
            label="Test Users"
            sx={{
              color: "white",
              fontSize: "14px",
              ml: 1,
              ".MuiFormControlLabel-label": {
                fontSize: "14px",
                color: "white",
              },
            }}
          />
        )}
      </Box>

      {/* Apply / Clear buttons */}
      <Box display="flex" width="max-content">
        <Button
          variant="contained"
          sx={{
            marginTop: "10px",
            marginBottom: "10px",
            marginRight: "10px",
            background: "#FFC107",
            color: "#0A0A0A",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#FFA000",
            },
          }}
          onClick={handleApply}
        >
          Apply
        </Button>
        <Button
          variant="outlined"
          sx={{
            marginTop: "10px",
            marginBottom: "10px",
            marginRight: "10px",
            outline: "1px solid #FFC107",
            color: "white",
            textTransform: "none",
            border: "none",
          }}
          onClick={handleClear}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
};

const SummaryChild = ({ setSummaryData, setFilters }) => {
  const { filterValues } = useListContext();

  // Pass filters to parent when they change
  useEffect(() => {
    setFilters(filterValues);
  }, [filterValues, setFilters]);

  const coinsSumFn = async () => {
    try {
      const exportData = await gameCoinsSummary(filterValues);
      setSummaryData(exportData);
    } catch (error) {
      console.error("Error not sum the betamount:", error);
    }
  };

  useEffect(() => {
    if (filterValues) {
      coinsSumFn();
    }
  }, [filterValues]);

  return null;
};

export const GameCoinsList = () => {
  const { isLoading, sort, total, page, perPage, setPage, setPerPage } =
    useListController();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: userIdentity } = useGetIdentity();
  const [isExporting, setIsExporting] = useState(false);
  const [canMarkTestUsers, setCanMarkTestUsers] = useState(false);

  const navigate = useNavigate();

  const [summaryData, setSummaryData] = useState(null);
  const [filters, setFilters] = useState({});
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [showExportSnackbar, setShowExportSnackbar] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // Check test user permission
  useEffect(() => {
    const checkPermission = async () => {
      try {
        const result = await Parse.Cloud.run("getTestUserPermission");
        setCanMarkTestUsers(result.canMarkTestUsers);
      } catch (error) {
        console.error("Failed to check test user permission:", error);
      }
    };

    if (userIdentity) {
      checkPermission();
    }
  }, [userIdentity]);

  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  // export the data in xls formate
  const handleExportXLS = async () => {
    try {
      setIsExporting(true);
      const exportData = await gamesCoinsExports(filters, sort);

      const totalRows = exportData.data.length;
      const selectedFields = [];

      for (let i = 0; i < totalRows; i++) {
        const item = exportData.data[i];
  
        const row = {
          GameName: item.gameName,
          TotalBetAmount: item.totalBetAmount,
          TotalWinAmount: item.totalWinAmount,
          NetAmount: item.netAmount,
          RTP: item.rtp,
        };
  
        // Conditionally add GameVariant if user is Super-User
        if (userIdentity?.userRoleName === "Super-User") {
          row.GameVariant = item.type;
        }
  
        selectedFields.push(row);
  
        // Progress bar update
        const progress = Math.floor((i / totalRows) * 80) + 10;
        setExportProgress(progress);
        await new Promise((res) => setTimeout(res, 1));
      }
  

      setExportProgress(95);

      const worksheet = XLSX.utils.json_to_sheet(selectedFields);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Game Coins Records");

      const xlsData = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

      setExportProgress(98);

      saveAs(
        new Blob([xlsData], { type: "application/octet-stream" }),
        "GameCoinsRecords.xlsx"
      );

      setExportProgress(100);
      setTimeout(() => {
        setShowExportSnackbar(false);
      }, 500);
    } catch (error) {
      console.error("Error fetching gamesCoinsExports:", error);
    } finally {
      setIsExporting(false);
      handleMenuClose();
    }
  };

  const listActions = (
    <TopToolbar>
      <Button
        variant="contained"
        startIcon={<GetAppIcon />}
        sx={{
          background: "#FFC107",
          color: "#0A0A0A",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#FFA000",
          },
        }}
        onClick={handleMenuOpen}
      >
        Export
      </Button>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        sx={{ marginTop: 1 }}
      >
        <MenuItem onClick={handleExportXLS} disabled={isExporting}>
          <ListItemIcon>
            <BackupTableIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {isExporting ? "Exporting..." : "Excel file"}
          </Typography>
        </MenuItem>
      </Menu>
    </TopToolbar>
  );

  if (isLoading) {
    return <Loader />;
  }
  console.log(userIdentity,"userIdentity")
  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 80px)",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
          }}
        >
          <Summary
            summaryData={summaryData}
            role={userIdentity?.userRoleName}
          />
        </Box>
        <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", pt: 2 }}>
          <Box
            sx={{
              mt: 2,
              backgroundColor: "#242424",
              borderRadius: 2,
              boxShadow: "none !important",
              padding: isSmallScreen ? { xs: 0, sm: 0 } : { xs: 1.5, sm: 2 },
            }}
          >
            <Snackbar
              open={showExportSnackbar}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              onClose={(event, reason) => {
                if (reason === "clickaway") return; // prevent closing on outside click
                setShowExportSnackbar(false); // allow only manual or timer-based close
              }}
              autoHideDuration={null}
            >
              <Alert
                severity="info"
                icon={false}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  px: 2,
                }}
              >
                <Box display="flex" flexDirection="column">
                  <Typography variant="body2" mb={1}>
                    Exporting... {exportProgress}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={exportProgress}
                    sx={{ width: "100%", height: 6, borderRadius: 5 }}
                  />
                </Box>
              </Alert>
            </Snackbar>

            <List
              title="Game Coins"
              actions={listActions}
              filters={<CustomFilter canMarkTestUsers={canMarkTestUsers} />}
              pagination={false}
              sort={{ field: "totalBetAmount", order: "DESC" }}
              sx={{
                "& .RaList-actions": {
                  flexWrap: "nowrap",
                },
                "& .RaFilterFormInput-spacer": { display: "none" },
              }}
            >
              <SummaryChild
                setSummaryData={setSummaryData}
                setFilters={setFilters}
              />
              <Box
                style={{
                  width: "100%",
                  overflowX: "auto",
                }}
              >
                <Box
                  style={{
                    width: "100%",
                    position: isSmallScreen ? "absolute" : "relative",
                  }}
                >
                  <Datagrid
                    bulkActionButtons={false}
                    rowClick={(id, basePath, record) => {
                      navigate(
                        `/gameCoins/${id}?gameName=${
                          record.gameName
                        }&gameType=${record.type}&startDate=${
                          filters.startDate || ""
                        }&endDate=${filters.endDate || ""}&manager=${filters.manager || ""}`
                      );
                      return false;
                    }}
                    sx={{
                      overflowX: "auto",
                      overflowY: "hidden",
                      width: "100%",
                      maxHeight: "100%",
                      "& .RaDatagrid-table": {
                        width: "100%",
                      },
                    }}
                  >
                    <TextField source="gameName" label="Game Name" />
                    {userIdentity?.userRoleName === "Super-User" && (
                      <FunctionField
                        label="Game Variant"
                        source="type"
                        render={(record) => {
                          const getColor = (status) => {
                            switch (status) {
                              case "TD":
                                return "secondary";
                              case "VE":
                                return "primary";
                              default:
                                return "default";
                            }
                          };
                          return (
                            <Chip
                              label={record.type}
                              color={getColor(record.type)}
                              size="small"
                              variant="outlined"
                            />
                          );
                        }}
                      />
                    )}

                    <NumberField
                      source="totalBetAmount"
                      label="Bet Amount"
                      textAlign="left"
                      options={{
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }}
                    />
                    <NumberField
                      source="totalWinAmount"
                      label="Win Amount"
                      textAlign="left"
                      options={{
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }}
                    />
                    <FunctionField
                      source="netAmount"
                      label="Net win"
                      render={(record) => {
                        const isNegative = record.netAmount < 0;
                        const color = isNegative ? "error" : "success";
                        const formatted =
                          record.netAmount?.toFixed(2) ?? "0.00";

                        return (
                          <Chip
                            label={formatted}
                            color={color}
                            size="small"
                            variant="filled"
                          />
                        );
                      }}
                    />
                    <FunctionField
                      label="RTP"
                      source="rtp"
                      render={(record) => `${record.rtp?.toFixed(2)}%`}
                    />
                  </Datagrid>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      width: "100% !important",
                      margin: "16px 0px",
                    }}
                  >
                    <CustomPagination
                      page={page}
                      perPage={perPage}
                      total={total}
                      setPage={setPage}
                      setPerPage={setPerPage}
                    />
                  </Box>
                </Box>
              </Box>
            </List>
          </Box>
        </Box>
      </Box>
    </React.Fragment>
  );
};
